import { t } from '@lingui/core/macro';

import { Cache } from '@urql/exchange-graphcache';
import invariant from 'invariant';

import { ApprovalTargetLockState } from '@watershed/constants/dataGovApprovals';
import { PermissionType } from '@watershed/constants/permissions';
import { BadInputError } from '@watershed/errors/BadInputError';
import { formatList } from '@watershed/intl/formatters';
import useLocale from '@watershed/intl/frontend/useLocale';
import {
  DataApprovalsFiles,
  DataGovApprovalsButtonType,
  DataGovApprovalsLockingButtonType,
  DataGovApprovalViewState,
  DataGovDatasourceUploadsApprovalSettings,
} from '@watershed/shared-universal/dataApprovals/dataGovApprovalsTypes';
import {
  getErroredUploads,
  getIncompleteUploads,
  getProcessingUploads,
} from '@watershed/shared-universal/measurement/userUploadUtils';
import assertNever from '@watershed/shared-util/src/assertNever';

import {
  GQChangelogEventPartsFragment,
  GQDataApprovalObjectChangelogQuery,
  GQDataApprovalUserUploadTaskChangelogQuery,
  GQUserUploadForDatasourceFragment,
  GQUserUploadTaskWithDatasourceFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';
import {
  GQApprovalStatus,
  GQObjectType,
} from '@watershed/app-dashboard/generated/graphql-schema-types';
import {
  DataApprovalObjectChangelogDocument,
  DataApprovalUserUploadTaskChangelogDocument,
} from '@watershed/app-dashboard/generated/urql';

import { useUserContext } from '../../../../../../utils/UserContext';
import useHasPermission from '../../../../../../utils/useHasPermission';
import { useProjectLockedTooltip } from '../../../TaskOrProjectLockedTooltip';

/**
 * For Data Approvals views to pass in given GraphQL, call hooks, and get back the settings
 * for the UI re: which buttons/state/tooltips/controls to show the user
 * (based on the user's permissions, the tasks's state, and what files are uploaded)
 *
 * This pulls relevant data out of GraphQL and passes to our test/server-friendly helper method.
 */
export function useApprovalViewSettingsForDatasourceUploads({
  userUploads,
  userUploadTask,
}: {
  userUploads: Array<GQUserUploadForDatasourceFragment>;
  userUploadTask: Pick<
    GQUserUploadTaskWithDatasourceFragment,
    | 'approvalStatus'
    | 'approvers'
    | 'datasource'
    | 'measurementProject'
    | 'hasIncompleteBuildings'
    | 'facilitiesPreview'
  >;
}): DataGovDatasourceUploadsApprovalSettings {
  const locale = useLocale();

  const {
    approvalStatus,
    approvers,
    datasource: { id: datasourceId, dataset },
    measurementProject,
    facilitiesPreview,
    hasIncompleteBuildings,
  } = userUploadTask;

  const hasPermissionToApproveData =
    useHasPermissionToApproveDatasource(datasourceId);
  const hasPermissionToSubmit =
    useHasPermissionsToSubmitDatasourceForApproval(datasourceId);

  const userContext = useUserContext();
  const projectLockedTooltip = useProjectLockedTooltip();

  const approversWithStatus = approvers.map((a) => ({
    userId: a.user.id,
    approvalStatus: a.approvalStatus,
    displayName: a.user.displayName,
  }));

  return getApprovalViewSettingsForDatasourceUploads({
    approvalStatus,
    approvers: approversWithStatus,
    projectLockedTooltip,
    userId: userContext.userId,
    hasPermissionToApproveData,
    hasPermissionToSubmit,
    userUploads,
    hasIncompleteBuildings:
      dataset.canonicalDataset?.kind === 'Buildings' && hasIncompleteBuildings,
    facilitiesToBeApprovedCount:
      dataset.canonicalDataset?.kind === 'Buildings'
        ? facilitiesPreview.totalCount
        : 0,
    locale,
    approvalsEnabledForProject: measurementProject.isApprovalFlowEnabled,
  });
}

/**
 * Largely for testing! You probably want useApprovalViewSettingsForDatasourceUploads() if using in UI
 *
 * Abstracted out to allow passing in simpler data structures but still get back the settings
 * for the UI re: which buttons/state/tooltips/controls to show the user
 * (based on the user's permissions, the tasks's state, and what files are uploaded)
 */
export function getApprovalViewSettingsForDatasourceUploads({
  approvalStatus,
  approvers,
  projectLockedTooltip,
  userId,
  hasPermissionToApproveData,
  hasPermissionToSubmit,
  userUploads,
  hasIncompleteBuildings,
  facilitiesToBeApprovedCount,
  locale,
  approvalsEnabledForProject,
}: {
  approvalStatus: GQApprovalStatus;
  approvers: Array<{
    userId: string;
    approvalStatus: GQApprovalStatus | null;
    displayName: string;
  }>;
  projectLockedTooltip?: string;
  userId: string;
  hasPermissionToApproveData: boolean;
  hasPermissionToSubmit: boolean;
  userUploads: DataApprovalsFiles;
  hasIncompleteBuildings: boolean;
  facilitiesToBeApprovedCount: number;
  locale: string;
  approvalsEnabledForProject: boolean;
}): DataGovDatasourceUploadsApprovalSettings {
  const isAssignedToApproveData = approvers
    .map((a) => a.userId)
    .includes(userId);

  const approvalViewState: DataGovApprovalViewState =
    ((): DataGovApprovalViewState => {
      if (!approvalsEnabledForProject) {
        switch (approvalStatus) {
          case GQApprovalStatus.Approved:
          case GQApprovalStatus.SubmittedForApproval:
            return DataGovApprovalViewState.APPROVALS_DISABLED_COMPLETE;
          case GQApprovalStatus.NotReadyForApproval: {
            if (hasIncompleteBuildings) {
              return DataGovApprovalViewState.APPROVALS_DISABLED_FACILITIES_INCOMPLETE;
            }
            return DataGovApprovalViewState.APPROVALS_DISABLED_UPLOADING;
          }
        }
      }

      switch (approvalStatus) {
        case GQApprovalStatus.Approved:
          if (hasPermissionToApproveData && isAssignedToApproveData) {
            return DataGovApprovalViewState.APPROVED_CAN_REJECT;
          }
          return DataGovApprovalViewState.APPROVED;

        case GQApprovalStatus.SubmittedForApproval:
          const hasAlreadyApproved = approvers.some(
            (a) =>
              a.userId === userId &&
              a.approvalStatus === GQApprovalStatus.Approved
          );
          if (hasAlreadyApproved) {
            return DataGovApprovalViewState.APPROVED_AWAITING_OTHERS;
          }

          return hasPermissionToApproveData && isAssignedToApproveData
            ? DataGovApprovalViewState.SUBMITTED_HAS_PERMISSION
            : DataGovApprovalViewState.SUBMITTED_BUT_NO_PERMISSION;

        // NOTE: there is no explicit state for rejected. It will show as "can submit"
        // The key is to make sure we show the comments and events from rejection to communicate this.
        case GQApprovalStatus.NotReadyForApproval:
          if (!hasPermissionToSubmit) {
            return DataGovApprovalViewState.CANNOT_SUBMIT_NO_PERMISSION;
          }

          if (hasIncompleteBuildings) {
            return DataGovApprovalViewState.CANNOT_SUBMIT;
          }

          const hasActiveFiles = userUploads.length > 0;
          if (!hasActiveFiles && facilitiesToBeApprovedCount === 0) {
            return DataGovApprovalViewState.CAN_SUBMIT_EMPTY;
          }

          const hasIncompleteUploads =
            getIncompleteUploads(userUploads).length > 0;
          const hasProcessingUploads =
            getProcessingUploads(userUploads).length > 0;
          const hasErroredUploads = getErroredUploads(userUploads).length > 0;
          if (hasIncompleteUploads || hasErroredUploads || hasProcessingUploads)
            return DataGovApprovalViewState.CANNOT_SUBMIT;

          return DataGovApprovalViewState.CAN_SUBMIT;

        default:
          assertNever(approvalStatus);
      }
    })();

  let result: DataGovDatasourceUploadsApprovalSettings;
  // Based on the evaluated state, return configured settings
  switch (approvalViewState) {
    case DataGovApprovalViewState.SUBMITTED_BUT_NO_PERMISSION:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.APPROVAL_DROPDOWN,
        isDisabled: true,
        tooltip: t({
          message: `You are not assigned to approve this data.`,
          context: `Button tooltip`,
        }),
        calloutText: t({
          message: `Submitted for approval`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.SUBMITTED_HAS_PERMISSION:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.APPROVAL_DROPDOWN,
        isDisabled: false,
        calloutText: t({
          message: `Your approval is requested`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.APPROVED_AWAITING_OTHERS:
      const otherApprovers = approvers.filter(
        (a) => a.approvalStatus === 'SubmittedForApproval'
      );
      const formattedOtherApprovers = formatList(
        otherApprovers.map((a) => a.displayName),
        {
          style: 'short',
          type: 'conjunction',
          locale,
        }
      );
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.POST_APPROVAL_DROPDOWN,
        buttonColor: 'secondary',
        isDisabled: false,
        calloutText: t({
          message: `You approved this data. Awaiting approval from ${formattedOtherApprovers}`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.APPROVED:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.APPROVED,
        isDisabled: true,
        calloutText: t({
          message: `All reviewers have approved this data`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.APPROVED_CAN_REJECT:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.POST_APPROVAL_DROPDOWN,
        buttonColor: 'secondary',
        isDisabled: false,
        calloutText: t({
          message: `All reviewers have approved this data`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.CANNOT_SUBMIT:
      {
        const message = hasIncompleteBuildings
          ? t({
              message: `You may submit for approval once all facilities are complete.`,
              context: `Callout text`,
            })
          : t({
              message: `You may submit for approval once all files have finished processing without error.`,
              context: `Callout text`,
            });

        result = {
          approvalViewState,
          buttonType: DataGovApprovalsButtonType.SUBMIT_FOR_APPROVAL,
          isDisabled: true,
          calloutText: message,
          tooltip: message,
        };
      }
      break;
    case DataGovApprovalViewState.CANNOT_SUBMIT_NO_PERMISSION:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.SUBMIT_FOR_APPROVAL,
        isDisabled: true,
        calloutText: t({
          message: `Awaiting submission`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.CAN_SUBMIT:
      result = {
        approvalViewState,
        buttonColor: 'primary',
        buttonType: DataGovApprovalsButtonType.SUBMIT_FOR_APPROVAL,
        isDisabled: false,
        calloutText: t({
          message: `Upload the specified data and submit it for approval.`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.CAN_SUBMIT_EMPTY:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.SUBMIT_FOR_APPROVAL,
        isDisabled: false,
        tooltip: t({
          message: `If there is no data that applies here, submit empty data for the approver to review.`,
          context: `Button tooltip`,
        }),
        calloutText: t({
          message: `Upload the specified data and submit it for approval.`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.APPROVALS_DISABLED_UPLOADING:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.APPROVALS_DISABLED_COMPLETE_TASK,
        buttonColor: 'primary',
        isDisabled: false,
        calloutText: t({
          message: `Upload the specified data.`,
          context: `Callout text`,
        }),
      };
      break;
    case DataGovApprovalViewState.APPROVALS_DISABLED_FACILITIES_INCOMPLETE:
      const message = t({
        message: `You cannot complete this task until all facilities are complete.`,
        context: `Callout text`,
      });
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.APPROVALS_DISABLED_COMPLETE_TASK,
        buttonColor: 'primary',
        isDisabled: true,
        calloutText: message,
        tooltip: message,
      };
      break;
    case DataGovApprovalViewState.APPROVALS_DISABLED_COMPLETE:
      result = {
        approvalViewState,
        buttonType: DataGovApprovalsButtonType.APPROVALS_DISABLED_UNDO,
        buttonColor: 'secondary',
        isDisabled: false,
        calloutText: t({
          message: `Completed`,
          context: `Callout text`,
        }),
      };
      break;
    default:
      throw new BadInputError('Add support state for ' + approvalViewState);
  }
  if (projectLockedTooltip) {
    result.isDisabled = true;
    result.tooltip = projectLockedTooltip;
  }
  return result;
}

export function useLockingViewSettingsForUserUploadTask({
  userUploadTask,
}: {
  userUploadTask: Pick<
    GQUserUploadTaskWithDatasourceFragment,
    | 'approvalStatus'
    | 'approvers'
    | 'datasource'
    | 'lockState'
    | 'measurementProject'
  >;
}): {
  lockState: ApprovalTargetLockState;
  isDisabled: boolean;
  buttonType: DataGovApprovalsLockingButtonType;
  tooltip: string;
} | null {
  const {
    approvalStatus,
    approvers,
    datasource: { id: datasourceId },
    lockState,
    measurementProject,
  } = userUploadTask;
  const userContext = useUserContext();
  const hasPermissionToUnlock = useHasPermission(
    PermissionType.ManageMeasurement
  );
  const hasPermissionToApproveData =
    useHasPermissionToApproveDatasource(datasourceId);
  const hasPermissionToSubmit =
    useHasPermissionsToSubmitDatasourceForApproval(datasourceId);

  const projectLockedTooltip = useProjectLockedTooltip();

  const approverIds = approvers.map((approver) => approver.user.id);
  const anyApproverHasApproved = approvers.some(
    (approver) => approver.approvalStatus === GQApprovalStatus.Approved
  );

  return getLockingViewSettings({
    approvalStatus,
    lockState,
    approverIds,
    userId: userContext.userId,
    hasPermissionToUnlock,
    hasPermissionToApproveData,
    hasPermissionToSubmit,
    anyApproverHasApproved,
    projectLockedTooltip,
    approvalsEnabledForProject: measurementProject.isApprovalFlowEnabled,
  });
}

export function getLockingViewSettings({
  approvalStatus,
  lockState,
  approverIds,
  userId,
  hasPermissionToUnlock,
  hasPermissionToApproveData,
  hasPermissionToSubmit,
  anyApproverHasApproved,
  projectLockedTooltip,
  approvalsEnabledForProject,
}: {
  approvalStatus: GQApprovalStatus;
  lockState: ApprovalTargetLockState;
  approverIds: Array<string>;
  userId: string;
  hasPermissionToUnlock: boolean;
  hasPermissionToApproveData: boolean;
  hasPermissionToSubmit: boolean;
  anyApproverHasApproved: boolean;
  projectLockedTooltip?: string;
  approvalsEnabledForProject: boolean;
}): {
  lockState: ApprovalTargetLockState;
  isDisabled: boolean;
  buttonType: DataGovApprovalsLockingButtonType;
  tooltip: string;
} | null {
  const isAssignedToApproveData = approverIds.includes(userId);
  const isAssignedAndAbleToApproveData =
    hasPermissionToApproveData && isAssignedToApproveData;

  let result: {
    lockState: ApprovalTargetLockState;
    isDisabled: boolean;
    buttonType: DataGovApprovalsLockingButtonType;
    tooltip: string;
  } | null;
  if (
    approvalStatus === GQApprovalStatus.Approved ||
    approvalStatus === GQApprovalStatus.SubmittedForApproval
  ) {
    invariant(
      lockState !== ApprovalTargetLockState.Unlocked,
      'Data must be locked if already approved or submitted for approval'
    );
    if (!approvalsEnabledForProject) {
      if (hasPermissionToApproveData || hasPermissionToSubmit) {
        result = {
          lockState,
          isDisabled: !!projectLockedTooltip,
          buttonType:
            DataGovApprovalsLockingButtonType.APPROVALS_DISABLED_REOPEN,
          tooltip: t({
            message: `Reopen the task for editing.`,
            context: `Button tooltip`,
          }),
        };
      } else {
        result = null;
      }
    } else if (hasPermissionToUnlock) {
      // If the data is locked and approved or submitted for approval, uploaders can request unlock
      if (isAssignedToApproveData) {
        // Approvers should just submit a review; they shouldn't see a button to unlock without reviewing
        result = null;
      } else if (lockState === ApprovalTargetLockState.UnlockRequested) {
        result = {
          lockState,
          isDisabled: false,
          buttonType: DataGovApprovalsLockingButtonType.LOCK_TOGGLE,
          tooltip: t({
            message: `An uploader has requested to undo submission. Note that unsubmitting will remove existing approvals.`,
            context: `Button tooltip`,
          }),
        };
      } else {
        result = {
          lockState,
          isDisabled: false,
          buttonType: DataGovApprovalsLockingButtonType.LOCK_TOGGLE,
          tooltip: t({
            message: `Undo submission to enable editing. Note that unsubmitting will remove existing approvals.`,
            context: `Button tooltip`,
          }),
        };
      }
    } else if (isAssignedAndAbleToApproveData) {
      if (lockState === ApprovalTargetLockState.UnlockRequested) {
        result = null;
      } else {
        result = null;
      }
    } else if (hasPermissionToSubmit) {
      if (!anyApproverHasApproved) {
        result = {
          lockState,
          isDisabled: false,
          buttonType: DataGovApprovalsLockingButtonType.UPLOADER_UNLOCK,
          tooltip: t({
            message: `Undo submission to enable editing.`,
            context: `Button tooltip`,
          }),
        };
      } else if (lockState === ApprovalTargetLockState.UnlockRequested) {
        result = {
          lockState,
          isDisabled: true,
          buttonType: DataGovApprovalsLockingButtonType.UNLOCK_REQUEST,
          tooltip: t({
            message: `Unsubmit request pending`,
            context: `Button tooltip`,
          }),
        };
      } else {
        result = {
          lockState,
          isDisabled: false,
          buttonType: DataGovApprovalsLockingButtonType.UNLOCK_REQUEST,
          tooltip: t({
            message: `Request to undo submission. Note that unsubmitting will remove existing approvals.`,
            context: `Button tooltip`,
          }),
        };
      }
    } else {
      result = null;
    }
    if (projectLockedTooltip && result) {
      result.isDisabled = true;
      result.tooltip = projectLockedTooltip;
    }

    return result;
  } else {
    return null;
  }
}

function useHasPermissionsToSubmitDatasourceForApproval(
  datasourceId: string
): boolean {
  return useHasPermission(PermissionType.ManageDatasource, {
    source: { id: datasourceId },
  });
}

function useHasPermissionToApproveDatasource(datasourceId: string): boolean {
  return useHasPermission(PermissionType.ApproveDatasource, {
    source: { id: datasourceId },
  });
}

function concatEvents(
  existingEvents: Array<GQChangelogEventPartsFragment>,
  newEvents: Array<GQChangelogEventPartsFragment>
): Array<GQChangelogEventPartsFragment> {
  const latestCachedEventTime = existingEvents[0]?.eventTime;
  // Safeguard against duplicating events that were already present in the changelog
  const trulyNewEvents = newEvents.filter(
    (e) => !latestCachedEventTime || e.eventTime > latestCachedEventTime
  );
  return trulyNewEvents.concat(existingEvents);
}

// Helper for updating the urql cache to insert new changelog entries returned by various mutations
export function updateDataApprovalUserUploadTaskChangelogCache(
  cache: Cache,
  targetKind: string,
  targetId: string,
  newChangelogEvents: Array<GQChangelogEventPartsFragment>
) {
  if (targetKind !== 'UserUploadTask') {
    return;
  }
  cache.updateQuery(
    {
      query: DataApprovalUserUploadTaskChangelogDocument,
      variables: {
        input: { userUploadTaskId: targetId },
      },
    },
    (data: GQDataApprovalUserUploadTaskChangelogQuery | null) => {
      if (data) {
        const existingEvents =
          data.dataApprovalUserUploadTaskChangelog.changelogEvents;
        data.dataApprovalUserUploadTaskChangelog.changelogEvents = concatEvents(
          existingEvents,
          newChangelogEvents
        );
      }
      return data;
    }
  );
}

export function updateDataApprovalObjectChangelogCache(
  cache: Cache,
  objectId: string,
  objectType: GQObjectType,
  newChangelogEvents: Array<GQChangelogEventPartsFragment>
) {
  cache.updateQuery(
    {
      query: DataApprovalObjectChangelogDocument,
      variables: {
        input: { objectId, objectType },
      },
    },
    (data: GQDataApprovalObjectChangelogQuery | null) => {
      if (data) {
        const existingEvents = data.dataApprovalObjectChangelog.changelogEvents;
        data.dataApprovalObjectChangelog.changelogEvents = concatEvents(
          existingEvents,
          newChangelogEvents
        );
      }
      return data;
    }
  );
}

export const exportedForTesting = {
  useHasPermissionsToSubmitDatasourceForApproval,
  useHasPermissionToApproveDatasource,
};
